import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Card,
  Box,
  Typography,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import ArrowForward from "@mui/icons-material/ArrowForward";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import { API_ENDPOINTS } from "../../../config/api";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const API_URL = API_ENDPOINTS.DEALER_QUOTATIONS;

const DealerQuotationDetailsContent = () => {
  const { id } = useParams();
  const [dealerQuotation, setDealerQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDealerQuotation();
  }, []);

  const fetchDealerQuotation = async () => {
    try {
      const response = await axios.get(API_URL.GET(id));
      setDealerQuotation(response.data.quotation);
      setLoading(false);
    } catch (error) {
      setError("Failed to load dealer quotation details. Please try again.");
      setLoading(false);
    }
  };

  if (loading) {
    return <CircularProgress sx={{ display: "block", margin: "auto", mt: 5 }} />;
  }

  if (error) {
    return (
      <Typography variant="h6" sx={{ textAlign: "center", mt: 5, color: "error.main" }}>
        {error}
      </Typography>
    );
  }

  if (!dealerQuotation) {
    return (
      <Typography variant="h6" sx={{ textAlign: "center", mt: 5 }}>
        Quotation not found
      </Typography>
    );
  }

  // Helper function to format detail rows
  const DetailRow = ({ label, value, boldValue = false, color = 'text.primary', highlight = false }) => (
    <Box sx={{
      display: 'flex',
      alignItems: 'baseline',
      gap: '8px',
      mb: 1,
      p: highlight ? 1 : 0,
      bgcolor: highlight ? 'action.hover' : 'transparent',
      borderRadius: highlight ? 1 : 0,
    }}>
      <Typography
        variant="body1"
        sx={{
          fontWeight: boldValue ? 'bold' : 'normal',
          color: boldValue ? color : 'text.secondary',
          whiteSpace: 'nowrap',
        }}
      >
        {label}:
      </Typography>
      <Typography
        variant="body1"
        sx={{
          fontWeight: boldValue ? 'bold' : 'normal',
          color: color,
          flex: 1,
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  const formatDeliveryDate = (order) => {
    if (!order) return 'N/A';
    if (order.delivery_date === 'Immediate') {
      return `Immediate ${order.delivery_date_hours ? order.delivery_date_hours + ' hrs' : ''}`;
    } else if (order.delivery_date === 'Within') {
      return `Within ${order.delivery_date_days ? order.delivery_date_days + ' days' : ''}`;
    } else {
      return order.delivery_date || 'N/A';
    }
  };

  const calculateTotalProductCost = (products) => {
    return products.reduce((sum, product) => {
      const cost = parseFloat(product.cost) || 0;
      if (product.quantity !== null) {
        return sum + (parseFloat(product.quantity) || 0) * cost;
      } else if (product.pieces !== null) {
        return sum + cost;
      }
      return sum;
    }, 0);


   
     
  };

  const totalProductCost = calculateTotalProductCost(dealerQuotation.dealer_quotation_products || []);
  const bendingCharges = parseFloat(dealerQuotation.bending_charges) || 0;
  const transportCharges = parseFloat(dealerQuotation.transport_charges) || 0;
  const totalAmountExcludingGst = totalProductCost + bendingCharges + transportCharges;
  const gstAmountCalculated = totalAmountExcludingGst * 0.18;
  const grandTotalCalculated = totalAmountExcludingGst + gstAmountCalculated;

  const handlePrint = () => {
    // Create a new window with only the quotation content (excluding dealer and customer details)
    const printWindow = window.open('', '_blank');
    const quotationContent = document.querySelector('.quotation-content').innerHTML;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Dealer Quotation - ${dealerQuotation.id}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              line-height: 1.6;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin: 10px 0; 
            }
            th, td { 
              border: 1px solid #ddd; 
              padding: 8px; 
              text-align: left; 
            }
            th { 
              background-color: #f2f2f2; 
              font-weight: bold;
            }
            .text-right { text-align: right; }
            .text-left { text-align: left; }
            .price-table { margin-top: 20px; }
            .custom-table { margin-bottom: 20px; }
            .quotation-header { 
              text-align: center; 
              margin-bottom: 20px; 
              border-bottom: 2px solid #333;
              padding-bottom: 10px;
            }
            .quotation-header h2 { margin: 0; color: #333; }
            .quotation-header p { margin: 5px 0; color: #666; }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="quotation-header">
            <h2>Dealer Quotation</h2>
            <p><strong>ID:</strong> ${dealerQuotation.id}</p>
            <p><strong>Date:</strong> ${new Date(dealerQuotation.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
            <p><strong>Dealer:</strong> ${dealerQuotation.app_user.company_name || 'N/A'}</p>
            <p><strong>Customer:</strong> ${dealerQuotation.customer_order.app_user.company_name || 'N/A'}</p>
          </div>
          ${quotationContent}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    // Wait for content to load then print
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const handleDownload = async () => {
    let originalText = 'Download'; // Default text
    let downloadButton = null;
    
    try {
      // Show loading state
      downloadButton = document.querySelector('button[onclick*="handleDownload"]');
      if (downloadButton) {
        originalText = downloadButton.textContent || 'Download';
        downloadButton.textContent = 'Generating PDF...';
        downloadButton.disabled = true;
      }

      // Get the quotation content element
      const quotationElement = document.querySelector('.quotation-content');
      if (!quotationElement) {
        throw new Error('Quotation content not found');
      }

      // Create a temporary container for PDF generation
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.width = '800px';
      tempContainer.style.backgroundColor = 'white';
      tempContainer.style.padding = '20px';
      tempContainer.style.fontFamily = 'Arial, sans-serif';
      tempContainer.style.fontSize = '12px';
      tempContainer.style.lineHeight = '1.4';
      
      // Clone the content to avoid modifying the original
      const clonedContent = quotationElement.cloneNode(true);
      
      // Add header information
      const headerDiv = document.createElement('div');
      headerDiv.innerHTML = `
        <div style="text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 15px;">
          <h2 style="margin: 0; color: #333; font-size: 20px;">Dealer Quotation</h2>
          <p style="margin: 6px 0; color: #666; font-size: 12px;"><strong>ID:</strong> ${dealerQuotation.id}</p>
          <p style="margin: 6px 0; color: #666; font-size: 12px;"><strong>Date:</strong> ${new Date(dealerQuotation.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
          <p style="margin: 6px 0; color: #666; font-size: 12px;"><strong>Dealer:</strong> ${dealerQuotation.app_user.company_name || 'N/A'}</p>
          <p style="margin: 6px 0; color: #666; font-size: 12px;"><strong>Customer:</strong> ${dealerQuotation.customer_order.app_user.company_name || 'N/A'}</p>
        </div>
      `;
      
      // Append header and cloned content
      tempContainer.appendChild(headerDiv);
      // Force readable colors inside PDF
      const forceStyles = document.createElement('style');
      forceStyles.textContent = `
        * { color: #111 !important; }
        table { border-collapse: collapse; width: 100%; }
        th { background: #f2f2f2 !important; color: #111 !important; }
        .last-row { background: #1976d2 !important; }
        .last-row * { color: white !important; }
        .last-row td { color: white !important; }
      `;
      tempContainer.appendChild(forceStyles);
      tempContainer.appendChild(clonedContent);
      
      // Add to DOM temporarily
      document.body.appendChild(tempContainer);
      
      // Wait for content to render
      await new Promise(resolve => setTimeout(resolve, 300));
      
      try {
        // Generate PDF using jsPDF + html2canvas
        const canvas = await html2canvas(tempContainer, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          width: 800,
          height: tempContainer.scrollHeight,
          logging: false,
          removeContainer: false
        });
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');
        
        const imgWidth = 190; // A4 width - margins (210 - 20)
        const pageHeight = 277; // A4 height - margins (297 - 20)
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;
        
        // Add first page
        pdf.addImage(imgData, 'PNG', 10, 10, imgWidth, imgHeight);
        heightLeft -= pageHeight;
        
        // Add additional pages if needed
        while (heightLeft >= 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 10, position + 10, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }
        pdf.save(`dealer-quotation-${dealerQuotation.id}.pdf`);
        
      } catch (pdfError) {
        throw new Error('PDF generation failed: ' + pdfError.message);
      } finally {
        // Clean up
        document.body.removeChild(tempContainer);
      }
      
    } catch (error) {
      
      // Fallback: download as HTML
      try {
        const quotationContent = document.querySelector('.quotation-content').innerHTML;
        const htmlContent = `
          <!DOCTYPE html>
          <html>
            <head>
              <title>Dealer Quotation - ${dealerQuotation.id}</title>
              <meta charset="utf-8">
              <style>
                body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.6; }
                table { width: 100%; border-collapse: collapse; margin: 10px 0; }
                th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                th { background-color: #f2f2f2; font-weight: bold; }
                .text-right { text-align: right; }
                .text-left { text-align: left; }
                .price-table { margin-top: 20px; }
                .custom-table { margin-bottom: 20px; }
                .quotation-header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 10px; }
                .quotation-header h2 { margin: 0; color: #333; }
                .quotation-header p { margin: 5px 0; color: #666; }
                .last-row { background: #1976d2 !important; }
                .last-row * { color: white !important; }
                .last-row td { color: white !important; }
              </style>
            </head>
            <body>
              <div class="quotation-header">
                <h2>Dealer Quotation</h2>
                <p><strong>ID:</strong> ${dealerQuotation.id}</p>
                <p><strong>Date:</strong> ${new Date(dealerQuotation.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                <p><strong>Dealer:</strong> ${dealerQuotation.app_user.company_name || 'N/A'}</p>
                <p><strong>Customer:</strong> ${dealerQuotation.customer_order.app_user.company_name || 'N/A'}</p>
              </div>
              ${quotationContent}
            </body>
          </html>
        `;
        
        const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `dealer-quotation-${dealerQuotation.id}.html`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        alert('PDF generation failed. Downloaded as HTML instead. You can convert it to PDF using your browser\'s print function (Ctrl+P → Save as PDF).');
      } catch (fallbackError) {
        alert('Error generating PDF. Please try again or contact support.');
      }
    } finally {
      // Restore button state
      const downloadButton = document.querySelector('button[onclick*="handleDownload"]');
      if (downloadButton) {
        downloadButton.textContent = originalText || 'Download';
        downloadButton.disabled = false;
      }
    }
  };

  return (
    <Box component="section" className="invoice-wrapper">
      <Box component="div" sx={{ p: 3, maxWidth: "1200px", margin: "auto" }}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Dealer Details Card */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                boxShadow: 0,
                borderRadius: "8px",
                p: { xs: 2, sm: 4 },
                background: "white",
                border: "1px solid #555555",
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: "var(--blackColor)" }}>
                  Dealer Details
                </Typography>
              </Box>
              <Box>
                <Divider sx={{ mb: 3, borderColor: "var(--borderColor)" }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12}>
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "baseline", gap: "8px" }}>
                      {dealerQuotation.app_user?.profile_image ? (
                        <img
                          src={dealerQuotation.app_user.profile_image}
                          alt="Dealer Avatar"
                          style={{ borderRadius: "50%", width: 120, height: 120, objectFit: "cover" }}
                        />
                      ) : (
                        <Avatar sx={{ width: 120, height: 120, bgcolor: "#bdbdbd" }} />
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={12} sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}>
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
                        <Typography component="p" sx={{ fontWeight: "bold" }}>Name: </Typography>
                        <Typography component="p">{dealerQuotation.app_user?.company_name || "N/A"}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
                        <Typography component="p" sx={{ fontWeight: "bold" }}>Mobile: </Typography>
                        <Typography component="p">{dealerQuotation.app_user?.mobile || "N/A"}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
                        <Typography component="p" sx={{ fontWeight: "bold" }}>Email: </Typography>
                        <Typography component="p">{dealerQuotation.app_user?.email || "N/A"}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
                        <Typography component="p" sx={{ fontWeight: "bold" }}>Address: </Typography>
                        <Typography component="p">{[
                          dealerQuotation.app_user?.street_line,
                          dealerQuotation.app_user?.city,
                          dealerQuotation.app_user?.state,
                          dealerQuotation.app_user?.country,
                          dealerQuotation.app_user?.pincode,
                        ].filter(Boolean).join(", ") || "N/A"}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "baseline", gap: "8px" }}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        sx={{
                          textTransform: "capitalize",
                          borderRadius: "20px",
                          fontWeight: 600,
                          px: 2,
                          py: 0.5,
                          boxShadow: "none",
                          background: "linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)",
                          "&:hover": {
                            background: "linear-gradient(90deg, #1565c0 0%, #1e88e5 100%)",
                            boxShadow: "none",
                          },
                        }}
                        href={`/ecommerce/appUsers-list/details/${dealerQuotation.app_user?.id}`}
                      >
                        More Details
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Card>
          </Grid>
          {/* Customer Details Card */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                boxShadow: 0,
                borderRadius: "8px",
                p: { xs: 2, sm: 4 },
                background: "white",
                border: "1px solid #555555",
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: "var(--blackColor)" }}>
                  Customer Details
                </Typography>
              </Box>
              <Box>
                <Divider sx={{ mb: 3, borderColor: "var(--borderColor)" }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12}>
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "baseline", gap: "8px" }}>
                      {dealerQuotation.customer_order?.app_user?.profile_image ? (
                        <img
                          src={dealerQuotation.customer_order.app_user.profile_image}
                          alt="Customer Avatar"
                          style={{ borderRadius: "50%", width: 120, height: 120, objectFit: "cover" }}
                        />
                      ) : (
                        <Avatar sx={{ width: 120, height: 120, bgcolor: "#bdbdbd" }} />
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={12} sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}>
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
                        <Typography component="p" sx={{ fontWeight: "bold" }}>Name: </Typography>
                        <Typography component="p">{dealerQuotation.customer_order?.app_user?.company_name || "N/A"}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
                        <Typography component="p" sx={{ fontWeight: "bold" }}>Mobile: </Typography>
                        <Typography component="p">{dealerQuotation.customer_order?.app_user?.mobile || "N/A"}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
                        <Typography component="p" sx={{ fontWeight: "bold" }}>Email: </Typography>
                        <Typography component="p">{dealerQuotation.customer_order?.app_user?.email || "N/A"}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
                        <Typography component="p" sx={{ fontWeight: "bold" }}>Address: </Typography>
                        <Typography component="p">{[
                          dealerQuotation.customer_order?.app_user?.street_line,
                          dealerQuotation.customer_order?.app_user?.city,
                          dealerQuotation.customer_order?.app_user?.state,
                          dealerQuotation.customer_order?.app_user?.country,
                          dealerQuotation.customer_order?.app_user?.pincode,
                        ].filter(Boolean).join(", ") || "N/A"}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "baseline", gap: "8px" }}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        sx={{
                          textTransform: "capitalize",
                          borderRadius: "20px",
                          fontWeight: 600,
                          px: 2,
                          py: 0.5,
                          boxShadow: "none",
                          background: "linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)",
                          "&:hover": {
                            background: "linear-gradient(90deg, #1565c0 0%, #1e88e5 100%)",
                            boxShadow: "none",
                          },
                        }}
                        href={`/ecommerce/appUsers-list/details/${dealerQuotation.customer_order?.app_user?.id}`}
                      >
                        More Details
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Card>
          </Grid>
        </Grid>
        <Card
          className="quotation-content"
          sx={{
            boxShadow: 0,
            borderRadius: "8px",
            p: { xs: 2, sm: 4 },
            background: "white",
            border: "1px solid #555555",
          }}
        >
          {/* Title and Header */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', color: 'text.secondary' }}>
              <Typography variant="h6">ID: {dealerQuotation.id}</Typography>
              <Typography variant="h6">Date: {new Date(dealerQuotation.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Dealer: {dealerQuotation.app_user.company_name || 'N/A'}
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Customer: {dealerQuotation.customer_order.app_user.company_name || 'N/A'}
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ mb: 2 }} />

          {/* Products Table */}
          {dealerQuotation.dealer_quotation_products && dealerQuotation.dealer_quotation_products.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <TableContainer className="custom-table" component={Paper} sx={{ boxShadow: 1, borderRadius: "8px" }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: "#f0f0f0" }}>
                    <TableRow>
                      <TableCell className="text-left">Item</TableCell>
                      <TableCell className="text-left">Brand</TableCell>
                      <TableCell  align="right" className="text-right">Qty (Tons)</TableCell>
                      <TableCell  align="right" className="text-right">Pieces</TableCell>
                      <TableCell  align="right" className="text-right">Cost</TableCell>
                
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dealerQuotation.dealer_quotation_products.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell>{product.product_type?.name || 'N/A'}</TableCell>
                        <TableCell>{product.brand?.name || 'N/A'}</TableCell>
                        <TableCell className="text-right" align="right">{product.quantity !== null ? product.quantity : '0'}</TableCell>
                        <TableCell className="text-right" align="right">{product.pieces !== null ? product.pieces : '0'}</TableCell>
                        <TableCell className="text-right" align="right">₹ {product.quantity !== null ?  product.quantity * product.cost : product.cost}</TableCell>
              
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Charges and Delivery Information */}
          <Box sx={{ mb: 3 }}>
              <TableContainer className="price-table" component={Paper} sx={{ boxShadow: 1, borderRadius: "8px" }}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Bending Charges:</TableCell>
                      <TableCell align="right">₹ {bendingCharges.toFixed(2)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Transport Charges:</TableCell>
                      <TableCell align="right">₹ {transportCharges.toFixed(2)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Payment Terms:</TableCell>
                      <TableCell align="right">
                        {dealerQuotation.customer_order?.payment_terms === 'Credit'
                          ? `${dealerQuotation.customer_order.payment_terms} (${dealerQuotation.customer_order.payment_terms_description})`
                          : dealerQuotation.customer_order?.payment_terms || 'N/A'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Delivery Terms:</TableCell>
                      <TableCell align="right">
                        {dealerQuotation.customer_order?.delivery_terms === 'Delivered To'
                          ? `${dealerQuotation.customer_order?.delivery_terms || 'N/A'}${dealerQuotation.customer_order?.delivery_address ? ' - ' + dealerQuotation.customer_order.delivery_address : ''}`
                          : dealerQuotation.customer_order?.delivery_terms || 'N/A'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Delivery Condition:</TableCell>
                      <TableCell align="right">{dealerQuotation.customer_order?.delivery_conditions || 'N/A'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Delivery Date:</TableCell>
                      <TableCell align="right">{formatDeliveryDate(dealerQuotation.customer_order)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total Material Rs:</TableCell>
                      <TableCell align="right">₹ {totalAmountExcludingGst.toFixed(2)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>GST 18%:</TableCell>
                      <TableCell align="right">₹ {dealerQuotation.gst_amount}</TableCell>
                    </TableRow>
                    <TableRow className="last-row">
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>Grand Total (GST included):</TableCell>
                      <TableCell align="right" sx={{ color: 'primary.main' }}>₹ {dealerQuotation.total_amount}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
        </Card>
        
        {/* Print and Download Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3, mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{
              bgcolor: '#1976d2',
              '&:hover': { bgcolor: '#1565c0' },
              px: 3,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Print
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{
              borderColor: '#1976d2',
              color: '#1976d2',
              '&:hover': { 
                borderColor: '#1565c0',
                bgcolor: 'rgba(25, 118, 210, 0.04)'
              },
              px: 3,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Download
          </Button>
        </Box>
        
        <Box className="text-center" sx={{ mt: 3 }}>
          <Link className="position-button" to="/dealer-quotations" style={{ textDecoration: "none" }}>
            <Button
              variant="contained"
              color="secondary"
              endIcon={<ArrowForward />}
            >
              Back to List
            </Button>
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

export default DealerQuotationDetailsContent;